import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import { Condition } from 'types/GlobalKpiOption'

import Text, { TextProps } from 'components_new/atoms/Text'

import SimpleTable from 'components_new/molecules/SimpleTable'

import RowEditor from './RowEditor'

export interface InputTableRow {
  id: string
  title: string
  input: {
    align?: TextProps['align']
    endAdornment?: string | null
    error?: boolean
    helperText?: string
    value: string | null
  }
  condition?: Condition
}

interface InputTableProps<T extends InputTableRow> {
  edit: boolean
  onChange: (row: T, value: string) => void
  rows: T[]
  sx?: SxProps
}

function InputTable<T extends InputTableRow>(props: InputTableProps<T>) {
  const { edit, onChange, rows, sx } = props

  const [editedRows, setEditedRows] = useState<Record<string, string | null>>(
    () => Object.fromEntries(rows.map((row) => [row.id, row.input.value]))
  )

  const handleChange = (row: T, value: string) => {
    onChange(row, value)
    setEditedRows((prev) => ({
      ...prev,
      [row.id]: value
    }))
  }

  return (
    <SimpleTable
      rows={rows.map((row) => {
        const condition = row.condition ?? Condition.EQ

        if (edit) {
          return { cells: [
            <Text
              color={editedRows[row.id] ? 'text.disabled' : undefined}
              fontWeight={!editedRows[row.id] ? 'bold' : undefined}
              key={0}
              variant="body2"
              sx={{ mb: row.input.helperText ? 2 : undefined }}
            >
              {row.title}
            </Text>,
            <Text key={1} sx={{ mb: row.input.helperText ? 2 : undefined }}>
              {condition}
            </Text>,
            <RowEditor
              initialValue={editedRows[row.id] ?? ''}
              key={2}
              onDebouncedChange={(name) => handleChange(row, name)}
              endAdornment={row.input.endAdornment}
              error={row.input.error}
              helperText={row.input.helperText}
            />
          ] }
        }

        return { cells: [
          <Text
            color={row.input.value ? 'text.disabled' : undefined}
            fontWeight={!row.input.value ? 'bold' : undefined}
            key={0}
            variant="body2"
          >
            {row.title}
          </Text>,
          condition,

          <Text
            key={2}
            fontWeight="bold"
            variant="body2"
            align={row.input.align ?? 'left'}
          >
            {row.input.value}
            {row.input.endAdornment ? ` ${row.input.endAdornment}` : undefined}
          </Text>
        ] }
      })}
      sx={sx}
    />
  )
}

InputTable.displayName = 'InputTable'
export default InputTable
